import request from 'src/lib/request';
import constants from 'src/constants';
import utils from 'src/utils';
import { get as lodashGet } from 'lodash';
import env from 'src/env';

const { SHARE_TOKEN_PARAM } = constants.jobShare;
const {
  convertManufacturersResponseData,
  buildQuery,
  getProductCatalogUrl,
} = utils.api;

export default class ViewerApi {
  static getUserProfile() {
    return request.get(`${env.API_SERVER}/api/v1/profile`);
  }

  static getJobDetails(jobId, shareToken) {
    const opts = {};
    if (shareToken) opts.query = buildQuery(SHARE_TOKEN_PARAM, shareToken);
    return request.get(`${env.API_SERVER}/api/v1/jobs/${jobId}`, opts);
  }

  static getProducts(job) {
    const productsUrl = getProductCatalogUrl(job, 'products');
    return request.get(productsUrl, { withCredentials: false });
  }

  static getManufacturers(job) {
    const manufacturersUrl = getProductCatalogUrl(job, 'manufacturers');
    return request
      .get(manufacturersUrl, { withCredentials: false })
      .then((response) => ({
        ...response,
        data: convertManufacturersResponseData(response.data),
      }));
  }

  static getJobDesigns(jobId, shareToken) {
    if (shareToken) return { data: [] };
    return request.get(`${env.API_SERVER}/api/v2/jobs/${jobId}/styles`);
  }

  static getJobDesignDetails({ jobId, designId }) {
    return request.get(
      `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${designId}`,
    );
  }

  static createDesign({ jobId, name }) {
    return request.post(`${env.API_SERVER}/api/v2/jobs/${jobId}/styles`, {
      data: {
        job_style: { name },
      },
    });
  }

  static createDesignHDF({ jobId, designId, hdfStr }) {
    var hdfBlob = new Blob([hdfStr], { type: 'application/json' });

    const data = new FormData();
    data.append('file', hdfBlob);

    const opts = {
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
    };

    return request.put(
      `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${designId}/hover_data_format`,
      opts,
    );
  }

  /*
   * https://developers.hover.to/#update-job-style
   */
  static updateSavedDesign({ jobId, designId, name }) {
    return request.put(
      `${env.API_SERVER}/api/v2/jobs/${jobId}/styles/${designId}`,
      {
        data: {
          job_style: { name },
        },
      },
    );
  }

  static uploadToS3(url, data) {
    return request.put(url, {
      headers: {
        'x-amz-acl': 'bucket-owner-full-control',
        'Cache-Control': 'public, must-revalidate, proxy-revalidate, max-age=0',
      },
      data,
    });
  }

  static updateFilesLoaded(url) {
    return request.put(url);
  }

  static getManufacturerProducts(job) {
    const manufacturerProductsUrl = getProductCatalogUrl(
      job,
      'manufacturerProducts',
    );
    return request.get(manufacturerProductsUrl, { withCredentials: false });
  }

  static getLabels() {
    return request
      .get(`${env.API_SERVER}/hover_models/labels.json`, {
        withCredentials: false,
      })
      .then((response) => {
        // Design calls for this to be named "Windows" but API calls it "Sash"
        const { data: origData } = response;
        const data = origData.map((item) => {
          return item.id === 405 ? { ...item, name: 'Windows' } : item;
        });
        return {
          ...response,
          data,
        };
      });
  }

  /**
   * POST request to create a new job share.
   * @param {Object} opts
   * @param {number} opts.jobId
   * @param {Blog} opts.imageBlob
   * @param {string} opts.shareToken
   */
  static createJobShare({ jobId, imageBlob, shareToken }) {
    const data = new FormData();
    data.append('job_share[job_id]', jobId);
    data.append('job_share[image]', imageBlob);
    data.append('job_share[viewer_version]', 'design_v2');

    const opts = {
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
    };

    if (shareToken) opts.query = buildQuery(SHARE_TOKEN_PARAM, shareToken);

    return request.post(`${env.API_SERVER}/api/v1/job_shares`, opts);
  }

  static updateJobShare(jobShareId, email, senderText) {
    return request.put(`${env.API_SERVER}/api/v1/job_shares/${jobShareId}`, {
      data: {
        job_share: {
          email,
          sender_text: senderText,
        },
      },
    });
  }
}
